import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { hiddenNavRoutes } from '../../utils/tenantNav';
import { useAlerts } from '../../contexts/AlertContext';
import { Menu, X } from 'lucide-react';

/**
 * Washi side rail.
 *
 * Desktop (lg+): a fixed 208px rail on the left. While it is mounted the body
 * carries `has-rail`, which index.css turns into left padding on #root, so the
 * 50+ pages that render <Navbar /> need no layout changes of their own.
 * Below lg: a slim top bar with a slide-out drawer holding the same groups.
 */

interface NavItem {
  label: string;
  route: string;
  tooltip: string;
  badge?: number;
}

interface NavGroup {
  title: string | null;
  items: NavItem[];
}

const RAIL_BODY_CLASS = 'has-rail';

const Seal: React.FC = () => (
  <span className="flex items-center justify-center w-7 h-7 bg-red-600 text-white font-serif text-base leading-none">
    安
  </span>
);

export const Navbar: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { unreadCount } = useAlerts();
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const showRail = isAuthenticated && !!user;

  const isActiveRoute = (route: string): boolean => {
    if (route === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname.startsWith('/carrier/');
    }
    if (route === '/peer-benchmark') {
      return location.pathname.startsWith('/peer-benchmark');
    }
    return location.pathname === route;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    setMobileMenuOpen(false);
  };

  // Offset page content for the fixed rail while it is mounted
  useEffect(() => {
    if (!showRail) return undefined;
    document.body.classList.add(RAIL_BODY_CLASS);
    return () => document.body.classList.remove(RAIL_BODY_CLASS);
  }, [showRail]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const isAdmin = user?.userType === 'admin' || user?.userType === 'government' || user?.role === 'super_admin';

  const groups: NavGroup[] = [
    {
      title: null,
      items: [
        { label: 'Overview', route: '/dashboard', tooltip: 'Your dashboard' },
        { label: 'Search', route: '/carrier-search', tooltip: 'Search carriers by DOT, name, or MC number' },
        { label: 'By Location', route: '/carriers-by-location', tooltip: 'Find all carriers in a given city or state' },
        { label: 'By Shipper', route: '/carriers-by-shipper', tooltip: 'Find all carriers that have hauled for a shipper (e.g. USPS)' },
        { label: 'Compare', route: '/carrier-compare', tooltip: 'Compare carriers side-by-side' },
        { label: 'Side by Side', route: '/carrier-comparison', tooltip: 'Compare 2-5 carriers side by side' },
        { label: 'Screen', route: '/bulk-screening', tooltip: 'Bulk carrier screening' },
        { label: 'Watchlist', route: '/monitored', tooltip: 'View and manage your monitored carriers' },
        { label: 'Watch Subscriptions', route: '/carrier-watchlist', tooltip: 'Manage your watchlist subscriptions' },
        { label: 'Alerts', route: '/alerts', tooltip: 'View notifications and alerts', badge: unreadCount }
      ]
    },
    {
      title: 'Intelligence',
      items: [
        { label: 'Analytics', route: '/statistics', tooltip: 'Platform statistics and trends' },
        { label: 'Scorecards', route: '/carrier-scorecard', tooltip: 'Carrier scorecards' },
        { label: 'Peer Benchmark', route: '/peer-benchmark', tooltip: 'Benchmark a carrier against its peers' },
        { label: 'Market', route: '/market-intelligence', tooltip: 'Advanced carrier search by location, fleet size, insurance, and ratings' },
        { label: 'Geography', route: '/geographic', tooltip: 'Geographic intelligence and carrier density analysis' },
        { label: 'Shipper Audit', route: '/shipper-audit', tooltip: 'Analyze shipper performance, carrier networks, and inspection patterns' },
        { label: 'DNU Analytics', route: '/dnu-analytics', tooltip: 'Do Not Use (DNU) compliance monitoring and analytics dashboard' },
        { label: 'VIN Lookup', route: '/vin-lookup', tooltip: 'Search VINs and detect fraud' },
        { label: 'VIN Matches', route: '/vin-matches', tooltip: 'View VIN matching analysis' },
        { label: 'Merge Carriers', route: '/merge', tooltip: 'Analyze carrier mergers and acquisitions' }
      ]
    },
    {
      title: 'Settings',
      items: [
        { label: 'Alert Preferences', route: '/alert-preferences', tooltip: 'Configure your alert settings' },
        { label: 'Tags', route: '/tags-management', tooltip: 'Manage inspection tags and categorize by shipper (USPS, FedEx, UPS, Amazon)' }
      ]
    },
    ...(isAdmin ? [{
      title: 'Administration',
      items: [
        { label: 'Admin', route: '/admin', tooltip: 'Admin Dashboard' },
        { label: 'Super Admin', route: '/super-admin', tooltip: 'Platform administration - Users, Companies, Analytics' },
        { label: 'Registrations', route: '/admin/registration-requests', tooltip: 'Review pending user registration requests' }
      ]
    }] : [])
  ];

  // Offer only the pages this tenant can actually open (see utils/tenantNav.ts).
  const hidden = hiddenNavRoutes(user);
  const visibleGroups = groups
    .map((group) => ({ ...group, items: group.items.filter((item) => !hidden.includes(item.route)) }))
    .filter((group) => group.items.length > 0);

  const renderGroups = () => visibleGroups.map((group, groupIndex) => (
    <div key={group.title ?? `group-${groupIndex}`} className={groupIndex > 0 ? 'mt-5' : ''}>
      {group.title && (
        <div className="mb-1.5 text-[11px] tracking-[0.18em] uppercase text-gray-400">{group.title}</div>
      )}
      {group.items.map((item) => {
        const active = isActiveRoute(item.route);
        return (
          <button
            key={item.route}
            onClick={() => navigate(item.route)}
            title={item.tooltip}
            aria-current={active ? 'page' : undefined}
            className={`relative w-full flex items-center justify-between py-[5px] text-left text-[13.5px] tracking-[0.02em] transition-colors ${
              active ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {active && <span className="absolute -left-7 top-1/2 w-4 h-px bg-red-600" aria-hidden="true" />}
            <span>{item.label}</span>
            {item.badge !== undefined && item.badge > 0 && (
              <span className="text-xs text-red-600 tabular-nums">{item.badge > 99 ? '99+' : item.badge}</span>
            )}
          </button>
        );
      })}
    </div>
  ));

  const brand = (
    <button
      onClick={() => navigate(isAuthenticated ? '/dashboard' : '/')}
      className="flex items-center gap-3 font-serif text-[17px] tracking-[0.12em] text-gray-900"
      aria-label="TSAP home"
    >
      <Seal />
      TSAP
    </button>
  );

  const accountFooter = user && (
    <div className="pt-4 border-t border-gray-200 text-xs">
      <div className="text-gray-700 truncate">{user.company_name}</div>
      <div className="text-gray-400 capitalize">{user.entity_type ?? user.userType}</div>
      <button onClick={handleLogout} className="mt-3 text-gray-500 hover:text-red-600 transition-colors">
        Sign out
      </button>
    </div>
  );

  // Signed-out pages keep a plain top bar with the brand only
  if (!showRail) {
    return (
      <nav className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center">{brand}</div>
      </nav>
    );
  }

  return (
    <>
      {/* Desktop rail */}
      {/* Rail stays at --z-rail (40) so modal backdrops (--z-modal, 70) cover it */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 z-40 w-[208px] flex-col bg-gray-50 border-r border-gray-200 px-7 pt-9 pb-6">
        {brand}
        {/* Only the links scroll; -mx-7/px-7 leaves room for the active marker, the mask fades the cut-off edge */}
        <nav
          className="mt-8 -mx-7 px-7 pb-6 flex-1 min-h-0 overflow-y-auto [mask-image:linear-gradient(to_bottom,black_calc(100%-24px),transparent)]"
          aria-label="Main"
        >
          {renderGroups()}
        </nav>
        <div className="pt-2">{accountFooter}</div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 bg-gray-50 border-b border-gray-200 h-14 px-5 flex items-center justify-between">
        {brand}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 text-gray-700"
          aria-label="Toggle mobile menu"
        >
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 bg-gray-900/30 transition-opacity duration-300 z-[calc(var(--z-drawer)-1)] lg:hidden ${
          mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMobileMenuOpen(false)}
      />
      <div
        className={`fixed top-0 left-0 h-full w-72 max-w-[85vw] bg-gray-50 border-r border-gray-200 z-[var(--z-drawer)] lg:hidden
          transform transition-transform duration-300 ease-out overflow-y-auto px-7 py-8
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="flex items-center justify-between">
          {brand}
          <button onClick={() => setMobileMenuOpen(false)} className="p-2 text-gray-500" aria-label="Close menu">
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="mt-8" aria-label="Main">{renderGroups()}</nav>
        <div className="mt-8">{accountFooter}</div>
      </div>
    </>
  );
};
